import React, { useCallback, useEffect } from 'react';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import classNames from 'classnames';

import { translate } from 'services/i18nService';
import GroupShowStore from 'stores/GroupShowStore';
import ModalService from 'services/ModalService';
import FirebaseStore from 'stores/FirebaseStore';
import ProfileStore from 'stores/ProfileStore';
import Button from 'components/Button/Button';
import ACCESS_LEVEL_NUM from 'enum/accessLevelsNum';
import { HOUR_AS_SECONDS, MINUTE_AS_MILLISECONDS } from 'enum/timeFormat';
import { NEW_SHOWS_STATE, BROADCAST_STATUS } from 'enum/videoPlayer';
import {
  generateID,
  getTimeStringWithHours,
  getTimeStringWithoutHours,
} from 'utils/helpers';
import { SecondsTimer } from 'utils/timers';

import groupShowTicketIcon from '../../../images/gs-icon.png';
import tokenIcon from '../../../images/icontokens.png';

import styles from './DesktopGroupTicketBar.css';


const IDS = {
  prefix: 'Fky9Is4d',
  element: 'DesktopGroupTicketBar',
  buyTicketButton: 'buyTicketButton',
  ticketGoal: 'ticketGoal',
  ticketBought: 'ticketBought',
};
const makeId = generateID(IDS.prefix, IDS.element);

const secondsTimer = new SecondsTimer();

const DesktopGroupTicketBar = (props) => {
  const {
    embedProfileV2,
    newShowsDetails,
    newShowsState,
    roomName,
    setShowRegistrationModal,
    showBuyTokensModal,
    user,
  } = props;
  const { accessLevel, username } = user || {};
  const isGuestBilling = accessLevel === ACCESS_LEVEL_NUM.GUEST_BILLING;

  const { groupShowTicketPrice } = FirebaseStore;

  const { isPlayerBroadcastMode } = ProfileStore;
  const {
    getRemainingTimeUntillGroupShow,
    remainingTimeLeftUntillGroupShow,
    userHaveTicket,
    setRemainingTimeGroupShow,
    setGroupShowTimer,
    groupShowTimer,
    setIsBonusTimeVisible,
    isBonusTimeVisible,
  } = GroupShowStore;

  const {
    bonusTime: newShowBonusTime,
    currentStart,
    currentDuration,
    goal,
    gsReservedTokens,
    gsTime,
    showDescription,
    ticketPrice,
  } = newShowsDetails || {};

  const areHoursVisible = Math.floor(remainingTimeLeftUntillGroupShow / HOUR_AS_SECONDS) > 0;
  let parsedSeconds = undefined;
  if (areHoursVisible) {
    parsedSeconds = getTimeStringWithHours(groupShowTimer);
  } else {
    parsedSeconds = getTimeStringWithoutHours(groupShowTimer);
  }

  const groupShowGoalTickets = goal / ticketPrice;
  const ticketsBought = gsReservedTokens ? gsReservedTokens / ticketPrice : 0;
  const bonusTime = newShowBonusTime && Math.round(newShowBonusTime / MINUTE_AS_MILLISECONDS);

  const isAdmin = user.accessLevel >= ACCESS_LEVEL_NUM.ADMIN;
  const noOfTicketsLeft = groupShowGoalTickets - ticketsBought;
  let ticketBtnLabel = undefined;
  if (isAdmin) {
    ticketBtnLabel = translate('v2profile.admin.ticket');
  } else if (noOfTicketsLeft === 0) {
    ticketBtnLabel = translate('v2profile.button.buyTicket');
  } else {
    ticketBtnLabel = groupShowTicketPrice;
  }

  const tickeWrapperClasses = classNames({
    [styles.ticketCount]: !userHaveTicket(username),
    [styles.ticketCountWithTicket]: userHaveTicket(username),
    [styles.disabled]: isPlayerBroadcastMode,
  });

  const disconnect = () => {
    const { setStreamHasEndedState, setBroadcastStatus } = ProfileStore;
    setBroadcastStatus(BROADCAST_STATUS.DISCONNECTED);
    setStreamHasEndedState(true);
  };

  useEffect(() => {
    getRemainingTimeUntillGroupShow(currentStart + currentDuration);
  }, [currentStart, currentDuration, getRemainingTimeUntillGroupShow]);

  const shouldCounterStart = (currentStart && remainingTimeLeftUntillGroupShow !== null);
  useEffect(() => {
    if (shouldCounterStart) {
      secondsTimer.start(setGroupShowTimer, remainingTimeLeftUntillGroupShow);
    }

    return () => secondsTimer.stop();
  }, [remainingTimeLeftUntillGroupShow, shouldCounterStart, setGroupShowTimer]);

  useEffect(() => {
    GroupShowStore.setIsBonusTimeVisible(false);
    const { activeModal, hideModal, MODAL_KEYS } = ModalService;
    if (activeModal?.modalKey === MODAL_KEYS.DESKTOP_COMPLETE_GOAL_MODAL) {
      hideModal();
    }
  }, []);

  useEffect(() => {
    const isNotInsideOrBonusOrPaused =
      newShowsState !== NEW_SHOWS_STATE.INSIDE_GS &&
      newShowsState !== NEW_SHOWS_STATE.BONUS_GS &&
      newShowsState !== NEW_SHOWS_STATE.PAUSED_GS;
    const isNotInSellingTicketMode =
      newShowsState !== NEW_SHOWS_STATE.GS_SELLING_TICKETS && newShowsState !== NEW_SHOWS_STATE.GS_GOAL_REACHED;
    const isGroupShowOver = isNotInsideOrBonusOrPaused && isNotInSellingTicketMode;
    const extendGroupShowTime = newShowsState === NEW_SHOWS_STATE.BONUS_GS;
    if (isGroupShowOver) {
      disconnect();
      setGroupShowTimer(0);
      setRemainingTimeGroupShow(0);
    }
    if (extendGroupShowTime && !isBonusTimeVisible) {
      setIsBonusTimeVisible(true);
      GroupShowStore.getRemainingTimeUntillGroupShow(currentStart + currentDuration);
    }
  }, [
    currentStart,
    currentDuration,
    groupShowTimer,
    isBonusTimeVisible,
    newShowsState,
    setGroupShowTimer,
    setIsBonusTimeVisible,
    setRemainingTimeGroupShow,
  ]);

  const onShowBuyTokensModal = useCallback(() => {
    if (embedProfileV2) {
      setShowRegistrationModal(true);
    } else {
      showBuyTokensModal();
    }
  }, [embedProfileV2, showBuyTokensModal, setShowRegistrationModal]);

  const onClickBuyTicket = useCallback(() => {
    if (!username) {
      onShowBuyTokensModal();
    } else if (isGuestBilling) {
      const modalKey = ModalService.MODAL_KEYS.GUEST_BILLING_MODAL;
      const modalProps = {
        close: ModalService.hideModal,
        show: true,
      };

      ModalService.showModal(modalKey, modalProps);
    } else {
      const modalKey = ModalService.MODAL_KEYS.GROUP_SHOW_BUY_TICKET_MODAL;
      const modalProps = {
        show: true,
        showBuyTokensModal,
        ticketPrice,
        showDescription,
        gsTime,
        roomName,
      };

      ModalService.showModal(modalKey, modalProps);
    }
  }, [
    isGuestBilling,
    gsTime,
    onShowBuyTokensModal,
    roomName,
    showBuyTokensModal,
    showDescription,
    ticketPrice,
    username,
  ]);

  const handleDescriptionTimerText = () => {
    const secondsToShow = groupShowTimer <= 0 ? '00:00' : parsedSeconds;
    switch (newShowsState) {
      case NEW_SHOWS_STATE.GS_GOAL_REACHED:
        return translate('v2profile.show.begin.timer', { 0: secondsToShow });
      case NEW_SHOWS_STATE.GS_SELLING_TICKETS:
        return translate('v2profile.show.goal.timer', { 0: secondsToShow });
      case NEW_SHOWS_STATE.INSIDE_GS:
      case NEW_SHOWS_STATE.BONUS_GS:
        return `${translate('newshows.newshows.show.ends.in')} ${secondsToShow}`;
      default:
        return '';
    }
  };

  const onClickBuyTicketWrapper = useCallback(() => {
    const hasTicket = userHaveTicket(username);
    if (hasTicket) {
      return;
    }

    onClickBuyTicket();
  }, [onClickBuyTicket, userHaveTicket, username]);

  const ticketsLeft = () => {
    if (noOfTicketsLeft <= 0) {
      return 0;
    }

    return noOfTicketsLeft;
  };

  const ticketsSold = noOfTicketsLeft === 0;

  return (
    <div className={styles.destkopTicketBarWrap}>
      <div className={styles.leftSide}>
        <img alt="group show" className={styles.groupShowIcon} src={groupShowTicketIcon} />
        <span className={styles.status}>{translate('directory.filter.show.type.group.show')}</span>
        <div
          className={tickeWrapperClasses}
          id={makeId(IDS.ticketGoal)}
          onClick={onClickBuyTicketWrapper}
        >
          {!ticketsSold && (
            <span className={styles.ticketsLeftText}>
              {translate('v2profile.groupshow.tickets')}: {ticketsLeft()}{' '}
              {translate('v2profile.groupshow.tickets.left')}
            </span>
          )}
          {userHaveTicket(username) ? (
            <Button
              className={styles.ticketBought}
              colour="orange"
              disabled={isPlayerBroadcastMode}
              icon={<CheckCircleIcon className={styles.ticketBoughtIcon} />}
              id={makeId(IDS.ticketBought)}
              label={translate('v2profile.group.show.ticket.purchased')}
              size="small"
            />
          ) : (
            <Button
              className={styles.ticketForSale}
              colour="orange"
              disabled={isPlayerBroadcastMode}
              icon={<img alt="tokens icon" className={styles.tokensImg} src={tokenIcon} />}
              id={makeId(IDS.buyTicketButton)}
              label={ticketBtnLabel}
              size="small"
              onClick={onClickBuyTicket}
            />
          )}
        </div>
      </div>
      <div className={styles.rightSide}>
        <AccessTimeIcon className={styles.timeIcon} />
        <span>
          {handleDescriptionTimerText()}{' '}
          {bonusTime && !isBonusTimeVisible && (
            <span className={styles.seconds}>
              {' '}
              + {bonusTime} {translate('newshows.newshows.mins')}
            </span>
          )}
        </span>
      </div>
    </div>
  );
};

DesktopGroupTicketBar.propTypes = {
  embedProfileV2: PropTypes.bool,
  newShowsDetails: PropTypes.object,
  newShowsState: PropTypes.string,
  roomName: PropTypes.string,
  setShowRegistrationModal: PropTypes.func,
  showBuyTokensModal: PropTypes.func,
  user: PropTypes.object,
};

DesktopGroupTicketBar.defaultProps = {
  embedProfileV2: false,
  newShowsDetails: undefined,
  newShowsState: undefined,
  roomName: undefined,
  setShowRegistrationModal: () => undefined,
  showBuyTokensModal: undefined,
  user: undefined,
};

export default observer(DesktopGroupTicketBar);
